import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  SEARCH_DOMAINS,
  isDemoMode,
  useClock,
  useSearch,
  type SearchDomain,
  type SearchRelatedBasis,
} from '../lib/data'
import { DataModeBadge } from '../components/HeaderBand'
import { DateScopeChip, DomainChips, RecentStrip, SearchField } from '../components/search/Chrome'
import {
  AskChatRow,
  BlockHeading,
  NoMatches,
  RelatedDivider,
  RelatedRow,
  ResultRow,
  ResultSkeleton,
  SummarizeRow,
  Unavailable,
} from '../components/search/parts'
import { exactBlockNote, resultsLine } from '../components/search/labels'

/**
 * SEARCH — docs/design/handoff/"LifeOS Search.dc.html", options 10a ("midterm") and 10b
 * ("video model motion quality"). One bar over five domains.
 *
 * THE ONE RULE THIS SCREEN IS BUILT AROUND (README §Interactions → Search): literal matches and
 * semantically related items are ranked and rendered in SEPARATE BLOCKS with explicit headings,
 * never interleaved, and the terms are highlighted in place. 10b is the state that proves it
 * matters — two items contain the words and the related block carries most of the answer — which
 * is why both options are implemented and neither is treated as the edge case.
 *
 * THE QUERY LIVES IN THE URL. `?q=` is the whole of this screen's state, which is what lets the
 * header search field on every other screen simply navigate here, lets a result be reached by
 * back button, and lets a search be pasted to someone. `?in=` carries the domain filter beside
 * it. Nothing here is remembered anywhere else.
 *
 * WHAT IS REAL, IN DEMO MODE. The literal half is a genuine search over the whole dataset —
 * every journal entry, transcript segment, save, task, deadline, announcement, syllabus fact and
 * learning item. The related half CANNOT be real without an embedding, so it is a curated list
 * for the two queries the handoff designs and an honest "arrives with V4" line for every other
 * one. See apps/web/src/lib/data/demo.ts → SEARCH for why faking it was not an option.
 */

/** README §Interactions → Search: "Debounce ~200 ms". */
const DEBOUNCE_MS = 200

/**
 * This tab's recent queries. Module-level rather than component state so it survives navigating
 * away and back, and NOT persisted anywhere: see RecentStrip for why a list of the owner's
 * questions is not something to write down as a side effect of a grey strip.
 */
const RECENT: string[] = []
const RECENT_MAX = 3

function remember(query: string) {
  const q = query.trim()
  if (!q) return
  const at = RECENT.indexOf(q)
  if (at !== -1) RECENT.splice(at, 1)
  RECENT.unshift(q)
  RECENT.length = Math.min(RECENT.length, RECENT_MAX)
}

/**
 * The two starter queries the no-matches card offers. They are honest suggestions rather than
 * semantic neighbours of whatever failed: both return real results against this dataset, and
 * neither claims to be related to the query that did not.
 */
const STARTERS = ['midterm', 'sublet'] as const

/**
 * WHAT AN EMPTY RELATED BLOCK MEANS — and it means three different things, so it says three
 * different sentences. Rendering the block empty, or printing one apology for all three, would
 * read as "there is nothing near this": a claim about meaning that two of these three states have
 * not computed (CLAUDE.md → Product identity rules: no invented thresholds).
 *
 *   'vector'      — the index was searched and came back empty. A real answer about meaning.
 *   'unavailable' in DEMO — there is no index behind this dataset and there never will be. The
 *                  demo's own sentence, unchanged from the port.
 *   'unavailable' in REMOTE — the index exists but did not answer for this search (a binding not
 *                  bound, Workers AI having a moment). The literal half above is unaffected.
 */
function emptyRelatedLine(basis: SearchRelatedBasis): string {
  if (basis === 'vector') {
    return (
      'Nothing in your corpus means the same thing without using these words. The semantic index ' +
      'was searched and came back empty — that is an answer, not a gap.'
    )
  }
  if (isDemoMode) {
    return (
      'Items that mean the same thing without using these words need the embeddings the AI layer ' +
      'writes on every save, and there are none behind this dataset. In the real app this block ' +
      'is filled by the search index; here it only fills in for searches a person wrote a list for.'
    )
  }
  return (
    'Items that mean the same thing without using these words need the semantic index, and it did ' +
    'not answer for this search. Everything above is a literal match and is unaffected.'
  )
}

const isDomain = (v: string | null): v is SearchDomain =>
  v !== null && (SEARCH_DOMAINS as readonly string[]).includes(v)

export function Search() {
  const [params, setParams] = useSearchParams()
  const urlQuery = params.get('q') ?? ''
  const active = isDomain(params.get('in')) ? (params.get('in') as SearchDomain) : null

  const [draft, setDraft] = useState(urlQuery)
  const { data: clock } = useClock()

  // ── the URL and the field, kept in step in both directions ────────────────
  //
  // FIELD → URL is debounced; URL → FIELD is not. Typing must not put a history entry (or a
  // request) behind every keystroke, but arriving with a query already in the URL — from the
  // header search field, a back button, a pasted link — has to land in the field immediately, or
  // the box shows one thing and the results show another.
  const lastUrl = useRef(urlQuery)
  useEffect(() => {
    if (urlQuery !== lastUrl.current) {
      lastUrl.current = urlQuery
      setDraft(urlQuery)
    }
  }, [urlQuery])

  useEffect(() => {
    if (draft === urlQuery) return
    const id = window.setTimeout(() => {
      lastUrl.current = draft
      // `replace` on purpose: a search is one destination being refined, not twelve. Without it,
      // one back press per character typed is the cost of leaving this screen.
      // The domain filter is dropped with the query — a lens chosen for one question is not a
      // lens for the next one, and silently keeping it hides results the owner never excluded.
      setParams(draft.trim() ? { q: draft } : {}, { replace: true })
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(id)
  }, [draft, urlQuery, setParams])

  const { data, isPending, error } = useSearch(urlQuery)
  const asked = urlQuery.trim().length > 0

  useEffect(() => {
    if (data && data.exact.length + data.related.length > 0) remember(data.query)
  }, [data])

  const pickDomain = useCallback(
    (next: SearchDomain | null) => {
      const merged: Record<string, string> = {}
      if (urlQuery.trim()) merged.q = urlQuery
      if (next) merged.in = next
      setParams(merged, { replace: true })
    },
    [urlQuery, setParams],
  )

  const run = useCallback((q: string) => {
    lastUrl.current = q
    setDraft(q)
    setParams({ q }, { replace: true })
  }, [setParams])

  const clear = useCallback(() => {
    lastUrl.current = ''
    setDraft('')
    setParams({}, { replace: true })
  }, [setParams])

  // The lens applies to BOTH blocks. Filtering only the literal half would leave the related
  // block showing a domain the chip says was excluded.
  const shown = useMemo(
    () => (data ? (active ? data.exact.filter((h) => h.domain === active) : data.exact) : []),
    [data, active],
  )
  const shownRelated = useMemo(
    () => (data ? (active ? data.related.filter((r) => r.domain === active) : data.related) : []),
    [data, active],
  )

  const today = clock?.date ?? null
  const total = data ? data.exact.length + data.related.length : 0
  const countLine = data && asked ? resultsLine(data.exact.length, data.related.length) : null

  // 10b's dark bar is offered ONLY when every result actually is a save. "Summarize what these 7
  // saves say about X" is a false description of a list that also holds a lecture and a deadline,
  // and a footer that lies about its own contents is worse than no footer.
  const allSaves =
    data !== null &&
    data !== undefined &&
    data.exact.length + data.related.length >= 3 &&
    [...data.exact, ...data.related].every((r) => r.domain === 'interests')

  return (
    <>
      <div
        className="flex flex-none flex-col gap-[13px] px-[30px] pb-4 pt-[26px]"
        style={{ background: 'var(--band-search)' }}
      >
        <SearchField
          value={draft}
          onChange={setDraft}
          onClear={clear}
          countLine={countLine}
          autoFocus
        />

        <div className="flex items-center gap-2">
          {data && asked ? (
            <DomainChips counts={data.counts} total={total} active={active} onPick={pickDomain} />
          ) : (
            // Before there is a query there is nothing to count, so the chips do not render a row
            // of zeroes that would read as "nothing matched" before anything was asked.
            <span className="text-[11.5px] font-normal leading-none text-muted">
              Journal · School · Interests · Reminders · Learning
            </span>
          )}
          {data && asked && <span className="mx-[3px] h-4 w-px flex-none bg-dash" aria-hidden />}
          {data && asked && <DateScopeChip />}

          {/* The demo badge rides this row on EVERY state, query or not: nobody is ever allowed
              to mistake this dataset for their own life (CLAUDE.md → Product identity rules), and
              a badge that disappears the moment the screen gets busy is a badge that is missing
              exactly when it matters. */}
          <div className="ml-auto flex min-w-0 items-center gap-3">
            <RecentStrip items={RECENT.filter((q) => q !== urlQuery.trim())} onPick={run} />
            <DataModeBadge />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-[9px] overflow-y-auto px-[30px] pb-[26px]">
        {!asked ? (
          <NoMatches query={null} suggestions={STARTERS} onSuggest={run} />
        ) : error ? (
          <Unavailable error={error} />
        ) : isPending || !data || !today ? (
          <>
            <BlockHeading label="EXACT MATCHES" />
            <ResultSkeleton />
          </>
        ) : data.exact.length === 0 && data.related.length === 0 ? (
          <NoMatches
            query={data.query}
            suggestions={RECENT.filter((q) => q !== data.query).slice(0, 2).length > 0
              ? RECENT.filter((q) => q !== data.query).slice(0, 2)
              : STARTERS}
            onSuggest={run}
          />
        ) : (
          <>
            <BlockHeading
              label="EXACT MATCHES"
              note={exactBlockNote(data.exact.length, data.terms.length)}
            />

            {shown.length === 0 ? (
              <p className="flex-none py-1 text-[12px] leading-[1.45] text-microcopy text-pretty">
                {active
                  ? 'Nothing in this domain contains those words. The other chips still do.'
                  : 'Nothing contains those words — but the related block below may still hold it.'}
              </p>
            ) : (
              shown.map((hit) => (
                <ResultRow key={hit.id} hit={hit} terms={data.terms} today={today} />
              ))
            )}

            <RelatedDivider />

            {shownRelated.length > 0 ? (
              shownRelated.map((item) => <RelatedRow key={item.id} item={item} today={today} />)
            ) : data.related.length > 0 ? (
              <p className="flex-none py-1 text-[12px] leading-[1.45] text-microcopy text-pretty">
                The related items for this search are in another domain. Clear the filter to see
                them.
              </p>
            ) : (
              /* THE HONEST VERSION OF AN EMPTY BLOCK, and it says a DIFFERENT thing depending on
                 whether anything was actually computed. Under 'vector' the index answered and had
                 no neighbours — a real answer about meaning. Under 'unavailable' nothing was
                 computed at all, and claiming "there is nothing near this" would be a claim this
                 build did not earn. One empty block, two sentences, never the wrong one. */
              <p className="flex-none rounded-panel border border-dashed border-accent-line bg-related-fill px-[17px] py-[13px] text-[11.5px] leading-[1.5] text-ink-3 text-pretty">
                {emptyRelatedLine(data.relatedBasis)}
              </p>
            )}

            {allSaves ? (
              <SummarizeRow
                count={data.exact.length + data.related.length}
                subject={data.query}
              />
            ) : (
              <AskChatRow
                tail={`end of ${data.exact.length + data.related.length} result${
                  data.exact.length + data.related.length === 1 ? '' : 's'
                }`}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}
